import { type FormEvent, useEffect, useState } from "react";
import { toast } from "react-toastify";
import type { Admin, Role } from "../config/Auth";
import { useAuth } from "../contexts/AuthContext";
import { createTeamMember, listTeam, resetTeamMemberPassword, updateTeamMember } from "../services/Api";

function errMessage(e: unknown): string {
  return (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? "Something went wrong.";
}

function formatWhen(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

export function Team() {
  const { admin: me } = useAuth();
  const [rows, setRows] = useState<Admin[] | null>(null);
  const [resetResult, setResetResult] = useState<{ username: string; new_password: string } | null>(null);

  // add-member form
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<Role>("editor");

  function load() {
    listTeam()
      .then((r) => setRows(r.data))
      .catch((e) => toast.error(errMessage(e)));
  }

  useEffect(load, []);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    try {
      await createTeamMember({
        username: username.trim(),
        email: email.trim(),
        password,
        display_name: displayName.trim() || null,
        role,
      });
      setUsername("");
      setEmail("");
      setDisplayName("");
      setPassword("");
      setRole("editor");
      toast.success("Member created.");
      load();
    } catch (e) {
      toast.error(errMessage(e));
    }
  }

  async function changeRole(member: Admin, newRole: Role) {
    try {
      await updateTeamMember(member.public_id, { role: newRole });
      toast.success("Role updated.");
      load();
    } catch (e) {
      toast.error(errMessage(e));
    }
  }

  async function toggleActive(member: Admin) {
    try {
      await updateTeamMember(member.public_id, { is_active: !member.is_active });
      load();
    } catch (e) {
      toast.error(errMessage(e));
    }
  }

  async function resetPassword(member: Admin) {
    if (!window.confirm(`Reset the password for ${member.username}?`)) return;
    try {
      const r = await resetTeamMemberPassword(member.public_id);
      setResetResult(r.data);
    } catch (e) {
      toast.error(errMessage(e));
    }
  }

  return (
    <div className="page">
      <div className="page-title-row">
        <h1>Team</h1>
      </div>

      {resetResult && (
        <div className="editor-message">
          New password for <b>{resetResult.username}</b>: <code>{resetResult.new_password}</code> — copy it now; it
          won't be shown again.{" "}
          <button className="btn-ghost" onClick={() => setResetResult(null)}>
            Dismiss
          </button>
        </div>
      )}

      <div className="users-layout">
        <div className="users-col">
          <div className="card">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last login</th>
                  <th aria-label="actions"></th>
                </tr>
              </thead>
              <tbody>
                {rows?.map((member) => {
                  const isSelf = member.public_id === me?.public_id;
                  return (
                    <tr key={member.public_id}>
                      <td>
                        <div className="cell-title">{member.display_name || member.username}</div>
                        <div className="muted">
                          {member.username} · {member.email}
                        </div>
                      </td>
                      <td>
                        <select
                          value={member.role}
                          disabled={isSelf}
                          onChange={(e) => changeRole(member, e.target.value as Role)}
                        >
                          <option value="editor">editor</option>
                          <option value="admin">admin</option>
                        </select>
                      </td>
                      <td>
                        {member.is_active ? (
                          <span className="badge badge-published">Active</span>
                        ) : (
                          <span className="badge badge-draft">Disabled</span>
                        )}
                      </td>
                      <td className="muted nowrap">{formatWhen(member.last_login_at)}</td>
                      <td className="cell-actions">
                        <button className="btn-ghost" onClick={() => resetPassword(member)}>
                          Reset password
                        </button>
                        {!isSelf && (
                          <button className="btn-ghost" onClick={() => toggleActive(member)}>
                            {member.is_active ? "Disable" : "Enable"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {rows && rows.length === 0 && (
                  <tr>
                    <td colSpan={5} className="muted">
                      No members yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="users-col">
          <form className="card card-pad" onSubmit={onCreate}>
            <h2 className="card-title">Add member</h2>
            <label className="field">
              <span className="field-label">Username</span>
              <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} required />
            </label>
            <label className="field">
              <span className="field-label">Email</span>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </label>
            <label className="field">
              <span className="field-label">Display name (optional)</span>
              <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
            </label>
            <label className="field">
              <span className="field-label">Temporary password (min 8)</span>
              <input type="text" value={password} onChange={(e) => setPassword(e.target.value)} minLength={8} required />
            </label>
            <label className="field">
              <span className="field-label">Role</span>
              <select value={role} onChange={(e) => setRole(e.target.value as Role)}>
                <option value="editor">Editor</option>
                <option value="admin">Admin</option>
              </select>
            </label>
            <button type="submit" className="btn-primary">
              Create member
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
